import log from 'loglevel';

import { getApiAddress } from '@/core/apiConfig';
import type { UserPermissionDto } from './mockupsShareDtos';
import {
  HideBlankStyleGetDto,
  type Share,
  type SharePermission,
  ShareResource,
} from './mockupsShareTypes';

const getDecoratorMockupsEndpoint = () => `${getApiAddress()}/decorator-mockups/v1`;

const parseResponse = (response: Response, parseJson = true) => {
  const { status } = response;

  if (status == 404) {
    return undefined;
  }

  if (status < 200 || status >= 400) {
    throw new Error(status.toString());
  }

  return parseJson ? response.json() : response;
};

/**
 * Creates or updates collection share permission.
 * @param id Collection ID.
 * @param permission Share permission.
 * @param email User email.
 * @param message Message for email.
 */
export const createOrUpdateCollectionShare = async (
  id: string,
  permission: SharePermission,
  email: string,
  message?: string,
) => {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');

  try {
    await fetch(`${getDecoratorMockupsEndpoint()}/collections/${id}/share/${permission}`, {
      body: JSON.stringify({ email, message }),
      credentials: 'include',
      headers,
      method: 'PUT',
    });
    // Clean liveblocks roomUsersInfoCache
    // LiveblocksService.roomUsersInfoCache = {};
  } catch (error) {
    if (!error.toString().includes('Failed to share')) {
      log.error(error);
    }
  }
};

/**
 * Get collection permission for me.
 * @param id Collection ID.
 */
export const getMyCollectionPermission = async (
  id: string,
): Promise<UserPermissionDto | undefined> => {
  try {
    const response = await fetch(`${getDecoratorMockupsEndpoint()}/collections/${id}/share/me`, {
      credentials: 'include',
    });
    return parseResponse(response);
  } catch (error) {
    log.error(error);
  }
  return undefined;
};

/**
 * Get collection shares.
 * @param id Collection ID.
 */
export const getCollectionShares = async (id: string): Promise<Share[]> => {
  try {
    const response = await fetch(`${getDecoratorMockupsEndpoint()}/collections/${id}/share`, {
      credentials: 'include',
    });
    return parseResponse(response);
  } catch (error) {
    log.error(error);
  }
  return undefined;
};

/**
 * Set collection share to organization (anyone in org can edit).
 * @param id Collection ID.
 * @param orgId Organization ID.
 */
export const setCollectionShareOrg = async (id: string, orgId: string): Promise<void> => {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  await fetch(`${getDecoratorMockupsEndpoint()}/collections/${id}/share-org/editor`, {
    body: JSON.stringify({ orgId }),
    credentials: 'include',
    headers,
    method: 'PUT',
  });
};

/**
 * Remove organization share from collection.
 * @param id Collection ID.
 * @param orgId Organization ID.
 */
export const deleteCollectionShareOrg = async (id: string, orgId: string): Promise<void> => {
  await fetch(`${getDecoratorMockupsEndpoint()}/collections/${id}/share-org/editor/${orgId}`, {
    credentials: 'include',
    method: 'DELETE',
  });
};

/**
 * Delete existing collection share.
 * @param id Collection ID.
 * @param permission Share permission.
 * @param email User email.
 */
export const deleteCollectionShare = async (
  id: string,
  permission: SharePermission,
  email: string,
) => {
  try {
    await fetch(`${getDecoratorMockupsEndpoint()}/collections/${id}/share/${permission}/${email}`, {
      credentials: 'include',
      method: 'DELETE',
    });
    // Clean liveblocks roomUsersInfoCache
    // LiveblocksService.roomUsersInfoCache = {};
  } catch (error) {
    if (!error.toString().includes('Failed to delete share')) {
      log.error(error);
    }
  }
};

/**
 * Creates or updates mockup share permission.
 * @param id Mockup ID.
 * @param permission Share permission.
 * @param email User email.
 * @param message Message for email.
 */
export const createOrUpdateMockupShare = async (
  id: string,
  permission: SharePermission,
  email: string,
  message?: string,
) => {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');

  try {
    await fetch(`${getDecoratorMockupsEndpoint()}/mockups/${id}/share/${permission}`, {
      body: JSON.stringify({ email, message }),
      credentials: 'include',
      headers,
      method: 'PUT',
    });
    // Clean liveblocks roomUsersInfoCache
    // LiveblocksService.roomUsersInfoCache = {};
  } catch (error) {
    if (!error.toString().includes('Failed to share')) {
      log.error(error);
    }
  }
};

/**
 * Get mockup permission for me.
 * @param id Mockup ID.
 */
export const getMyMockupPermission = async (id: string): Promise<UserPermissionDto | undefined> => {
  try {
    const response = await fetch(`${getDecoratorMockupsEndpoint()}/mockups/${id}/share/me`, {
      credentials: 'include',
    });
    return parseResponse(response);
  } catch (error) {
    log.error(error);
  }
  return undefined;
};

/**
 * Get mockup shares.
 * @param id Mockup ID.
 */
export const getMockupShares = async (id: string): Promise<Share[]> => {
  try {
    const response = await fetch(`${getDecoratorMockupsEndpoint()}/mockups/${id}/share`, {
      credentials: 'include',
    });
    return parseResponse(response);
  } catch (error) {
    log.error(error);
  }
  return undefined;
};

/**
 * Set mockup share to organization (anyone in org can edit).
 * @param id Mockup ID.
 * @param orgId Organization ID.
 */
export const setMockupShareOrg = async (id: string, orgId: string): Promise<void> => {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  await fetch(`${getDecoratorMockupsEndpoint()}/mockups/${id}/share-org/editor`, {
    body: JSON.stringify({ orgId }),
    credentials: 'include',
    headers,
    method: 'PUT',
  });
};

/**
 * Remove organization share from mockup.
 * @param id Mockup ID.
 * @param orgId Organization ID.
 */
export const deleteMockupShareOrg = async (id: string, orgId: string): Promise<void> => {
  await fetch(`${getDecoratorMockupsEndpoint()}/mockups/${id}/share-org/editor/${orgId}`, {
    credentials: 'include',
    method: 'DELETE',
  });
};

/**
 * Delete existing mockup share.
 * @param id Mockup ID.
 * @param permission Share permission.
 * @param email User email.
 */
export const deleteMockupShare = async (id: string, permission: SharePermission, email: string) => {
  try {
    await fetch(`${getDecoratorMockupsEndpoint()}/mockups/${id}/share/${permission}/${email}`, {
      credentials: 'include',
      method: 'DELETE',
    });
    // Clean liveblocks roomUsersInfoCache
    // LiveblocksService.roomUsersInfoCache = {};
  } catch (error) {
    if (!error.toString().includes('Failed to delete share')) {
      log.error(error);
    }
  }
};

const hideBlankStyleResourcePath = (asset: ShareResource) =>
  asset === ShareResource.COLLECTION ? 'collections' : 'mockups';

export const setHideBlankStyle = async (
  asset: ShareResource,
  id: string,
  enabled: boolean,
): Promise<void> => {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  try {
    await fetch(
      `${getDecoratorMockupsEndpoint()}/${hideBlankStyleResourcePath(
        asset,
      )}/${id}/hide-blank-style`,
      {
        body: JSON.stringify({ enabled }),
        credentials: 'include',
        headers,
        method: 'PUT',
      },
    );
  } catch (error) {
    log.error(error);
  }
};

export const getHideBlankStyle = async (
  asset: ShareResource,
  id: string,
): Promise<HideBlankStyleGetDto | undefined> => {
  try {
    const response = await fetch(
      `${getDecoratorMockupsEndpoint()}/${hideBlankStyleResourcePath(
        asset,
      )}/${id}/hide-blank-style`,
      { credentials: 'include' },
    );
    const data = await parseResponse(response);
    return data as HideBlankStyleGetDto;
  } catch (error) {
    log.error(error);
  }
  return undefined;
};
